import {
  Add as AddIcon,
  Groups as GroupsIcon,
  Lock as LockIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import * as labels from "config.labels";
import React from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const iconSx = { fontSize: "1rem", color: "text.disabled", flexShrink: 0 };

// Report Builder's landing page when there is no recently opened report to
// return to: the list of reports, already open, so nothing runs until the
// user picks one.
const ChooseReportPage = ({ reports = [], onSelect, onCreate }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  return (
    <Box
      data-testid={"choose-report-page"}
      sx={{ width: "100%", maxWidth: 760, mx: "auto", px: 3, py: 4 }}
    >
      <Stack
        direction={"row"}
        alignItems={"flex-end"}
        justifyContent={"space-between"}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant={"h5"} component={"h1"}>
            {translateLabels(labels.REPORTS_CHOOSE_REPORT_TITLE, {
              default: "Choose a report",
            })}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {translateLabels(labels.REPORTS_CHOOSE_REPORT_DESCRIPTION, {
              default: "Pick a report to open, or create a new one.",
            })}
          </Typography>
        </Box>
        <Button
          size={"small"}
          startIcon={<AddIcon />}
          onClick={onCreate}
          data-testid={"choose-report-new"}
          sx={{ flexShrink: 0 }}
        >
          {translateLabels(labels.REPORTS_CHOOSE_REPORT_NEW, {
            default: "New report",
          })}
        </Button>
      </Stack>
      <Paper variant={"outlined"}>
        <List
          data-testid={"choose-report-list"}
          aria-label={translateLabels(labels.REPORTS_CHOOSE_REPORT_TITLE, {
            default: "Choose a report",
          })}
          disablePadding
        >
          {reports.map((report) => (
            <ListItemButton
              key={report.internalName}
              data-testid={`choose-report-item-${report.internalName}`}
              onClick={() => onSelect?.(report.internalName)}
              divider
              sx={{ gap: 1 }}
            >
              <ListItemText
                primary={report.displayName}
                secondary={report.description || null}
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
              {report.multiOutputReport && (
                <TableChartIcon
                  sx={iconSx}
                  titleAccess={translateLabels(labels.REPORTS_NAV_MULTI_TABLE, {
                    default: "Multi-table report",
                  })}
                />
              )}
              {report.allUsers && (
                <GroupsIcon
                  sx={iconSx}
                  titleAccess={translateLabels(labels.REPORTS_NAV_ALL_USERS, {
                    default: "Shared with all users",
                  })}
                />
              )}
              {report.canWrite === false && (
                <LockIcon
                  sx={iconSx}
                  titleAccess={translateLabels(labels.REPORTS_NAV_READ_ONLY, {
                    default: "Read only",
                  })}
                />
              )}
            </ListItemButton>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default ChooseReportPage;
